import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Head, router } from '@inertiajs/react';
import AppShell, { type AppShellProps } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createConsumer } from "@rails/actioncable";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Guest {
  id: string | number;
  name: string;
  role: string;
  organization?: string;
}

interface Seat {
  seatNo: number;
  guest: Guest | null;
}

interface Table {
  id: string;
  label: string;
  notes: string;
  username?: string;
  password?: string;
  seatCount: number;
  quota: {
    admin: number;
    t1: number;
    t2: number;
  };
  seats: Seat[];
}

type TableRole = keyof Table['quota'];

interface Props extends Omit<AppShellProps, 'children'> {
  subdomain?: string;
  event: {
    id: number;
    name: string;
  };
  seatingData: Table[];
  seatingLog: any[];
  seatingRoleLabels?: Record<string, string>;
  checkedInIds?: number[];
}

const DEFAULT_ROLE_LABELS: Record<string, string> = {
  admin: 'إداري',
  t1: 'فني 1',
  t2: 'فني 2',
};

/* ------------------------------------------------------------------ */
/*  Floor Plan Configuration                                           */
/* ------------------------------------------------------------------ */

const FLOOR_SLOTS = (function () {
  const slots: [number, number][] = [];
  const rowCols = [11, 10, 9, 7, 6, 5, 4, 2, 1, 0];
  const rowColsReversed = [...rowCols].reverse();

  for (let row = 6; row >= 1; row--) {
    const visualRowIndex = 6 - row;
    const cols = visualRowIndex % 2 === 0 ? rowCols : rowColsReversed;
    cols.forEach(col => slots.push([row, col]));
  }

  const topRowCols = [11, 10, 9, 2, 1, 0];
  const topVisualRowIndex = 6;
  const orderedTopCols = topVisualRowIndex % 2 === 0 ? topRowCols : [...topRowCols].reverse();
  orderedTopCols.forEach(col => slots.push([0, col]));

  return slots;
})();

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Seats(props: Props) {
  const { subdomain, event, seatingData: initialData, seatingLog: initialLog, seatingRoleLabels, checkedInIds = [], urls, translations: t, ...shell } = props;
  const ROLE_LABELS = seatingRoleLabels || DEFAULT_ROLE_LABELS;

  const [tables, setTables] = useState<Table[]>(initialData);
  const [activity, setActivity] = useState<any[]>(initialLog);
  const [selTblId, setSelTblId] = useState<string | null>(initialData[0]?.id || null);
  const [selSeatNo, setSelSeatNo] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSuggOpen, setIsSuggOpen] = useState(false);
  const [activeResults, setActiveResults] = useState<any[]>([]);
  const [colorGuideOpen, setColorGuideOpen] = useState(false);
  const [moveTblId, setMoveTblId] = useState<string>('');
  const [moveSeatNo, setMoveSeatNo] = useState<number>(1);
  const [globalSeatCount, setGlobalSeatCount] = useState<number>(10);

  // Socket
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const cableProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const cableUrl = `${cableProtocol}://${window.location.host}/cable`;

    // @ts-ignore
    const consumer = createConsumer(cableUrl);
    channelRef.current = consumer.subscriptions.create(
      { channel: "SeatingChannel", event_id: event.id, subdomain: subdomain },
      {
        received: (data: any) => {
          if (data.type === 'UPDATE_SEATING') {
            setTables(data.tables);
            setActivity(data.log);
          }
        }
      }
    );

    return () => {
      channelRef.current?.unsubscribe();
    };
  }, [event.id, subdomain]);

  const sendUpdate = (newTables: Table[], logText: string) => {
    const ts = new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
    const logEntry = { ts, text: logText };
    const newLog = [logEntry, ...activity].slice(0, 80);
    
    // Optimistic update
    setTables(newTables);
    setActivity(newLog);

    // Send to server
    channelRef.current?.perform('update_seating', { tables: newTables, log: newLog });
  };

  // Helper selectors
  const selTbl = useMemo(() => tables.find(t => t.id === selTblId), [tables, selTblId]);
  const selSeat = useMemo(() => selTbl?.seats.find(s => s.seatNo === selSeatNo), [selTbl, selSeatNo]);

  // Statistics
  const stats = useMemo(() => {
    const ts = tables.reduce((s, t) => s + t.seatCount, 0);
    const occ = tables.reduce((s, t) => s + t.seats.filter(s => s.guest).length, 0);
    const warn = tables.filter(t => {
      const rc = roleCount(t);
      return rc.admin !== t.quota.admin || rc.t1 !== t.quota.t1 || rc.t2 !== t.quota.t2;
    }).length;
    const full = tables.filter(t => t.seats.filter(s => s.guest).length >= t.seatCount).length;
    
    return { totalSeats: ts, occupied: occ, issues: warn, full, ok: tables.length - warn - full };
  }, [tables]);

  function roleCount(t: Table) {
    const c = { admin: 0, t1: 0, t2: 0 };
    t.seats.forEach(s => {
      if (!s.guest) return;
      if (s.guest.role === 'admin') c.admin++;
      else if (s.guest.role === 't1') c.t1++;
      else if (s.guest.role === 't2') c.t2++;
    });
    return c;
  }

  function getTblStatus(t: Table) {
    if (t.seats.filter(s => s.guest).length < t.seatCount) return 'ok';
    const rc = roleCount(t);
    if (rc.admin !== t.quota.admin || rc.t1 !== t.quota.t1 || rc.t2 !== t.quota.t2) return 'warn';
    return 'full';
  }

  // Actions
  const handleAddTable = () => {
    const newId = `tbl-extra-${Date.now()}`;
    const newTable: Table = {
      id: newId,
      label: `طاولة ${tables.length + 1}`,
      notes: '',
      username: '',
      password: '',
      seatCount: globalSeatCount,
      quota: { admin: 0, t1: 0, t2: 0 },
      seats: Array.from({ length: globalSeatCount }, (_, i) => ({ seatNo: i + 1, guest: null }))
    };
    sendUpdate([...tables, newTable], `إضافة ${newTable.label}`);
    setSelTblId(newId);
    setSelSeatNo(1);
  };

  const handleRemoveTable = () => {
    if (!selTbl) return;

    const occupiedSeats = tblOccupied(selTbl);
    const confirmMessage = occupiedSeats > 0
      ? `سيتم حذف ${selTbl.label} وإلغاء توزيع ${occupiedSeats} مقعد/ضيف منها. هل تريد المتابعة؟`
      : `هل تريد حذف ${selTbl.label}؟`;

    if (!window.confirm(confirmMessage)) return;

    const currentIndex = tables.findIndex(t => t.id === selTbl.id);
    const newTables = tables.filter(t => t.id !== selTbl.id);
    const fallbackTable = newTables[Math.min(currentIndex, newTables.length - 1)];

    sendUpdate(newTables, `حذف ${selTbl.label}`);
    setSelTblId(fallbackTable?.id || null);
    setSelSeatNo(1);
  };

  const handleUpdateTable = (updates: Partial<Table>) => {
    if (!selTblId) return;
    const newTables = tables.map(t => t.id === selTblId ? { ...t, ...updates } : t);
    sendUpdate(newTables, `تحديث بيانات ${selTbl?.label}`);
  };

  const handleUpdateSeatCount = (nextSeatCount: number) => {
    if (!selTbl) return;

    const minSeatCount = Math.max(1, tblOccupied(selTbl));
    const normalizedSeatCount = Math.max(minSeatCount, nextSeatCount);
    if (normalizedSeatCount === selTbl.seatCount) return;

    const seats = normalizedSeatCount > selTbl.seatCount
      ? [
          ...selTbl.seats,
          ...Array.from({ length: normalizedSeatCount - selTbl.seatCount }, (_, index) => ({
            seatNo: selTbl.seatCount + index + 1,
            guest: null,
          })),
        ]
      : selTbl.seats.slice(0, normalizedSeatCount);

    // Auto-scale quota down if new seat count is below current quota total
    let quota = { ...selTbl.quota };
    const quotaTotal = tblQuotaTotal(selTbl);
    if (quotaTotal > normalizedSeatCount) {
      let excess = quotaTotal - normalizedSeatCount;
      (['t2', 't1', 'admin'] as TableRole[]).forEach(role => {
        const cut = Math.min(quota[role], excess);
        quota[role] -= cut;
        excess -= cut;
      });
    }

    handleUpdateTable({ seatCount: normalizedSeatCount, seats, quota });

    if (selSeatNo > normalizedSeatCount) {
      setSelSeatNo(normalizedSeatCount);
    }
  };

  const handleApplyGlobalSeatCount = () => {
    if (globalSeatCount < 1) return;
    const newTables = tables.map(t => {
      const minSeats = Math.max(1, tblOccupied(t));
      const newCount = Math.max(minSeats, globalSeatCount);
      const seats = newCount > t.seatCount
        ? [...t.seats, ...Array.from({ length: newCount - t.seatCount }, (_, i) => ({ seatNo: t.seatCount + i + 1, guest: null }))]
        : t.seats.slice(0, newCount);
      let quota = { ...t.quota };
      const qTotal = tblQuotaTotal(t);
      if (qTotal > newCount) {
        let excess = qTotal - newCount;
        (['t2', 't1', 'admin'] as TableRole[]).forEach(role => {
          const cut = Math.min(quota[role], excess);
          quota[role] -= cut;
          excess -= cut;
        });
      }
      return { ...t, seatCount: newCount, seats, quota };
    });
    sendUpdate(newTables, `تحديث عدد مقاعد جميع الطاولات إلى ${globalSeatCount}`);
  };

  const handleSetTableCount = (newCount: number) => {
    const clamped = Math.max(1, Math.min(FLOOR_SLOTS.length, newCount));
    if (clamped === tables.length) return;
    if (clamped < tables.length) {
      const toRemove = tables.slice(clamped);
      const occupiedCount = toRemove.reduce((sum, t) => sum + tblOccupied(t), 0);
      const msg = occupiedCount > 0
        ? `سيتم حذف ${toRemove.length} طاولة تحتوي على ${occupiedCount} مقعد مشغول. هل تريد المتابعة؟`
        : `سيتم حذف ${toRemove.length} طاولة. هل تريد المتابعة؟`;
      if (!window.confirm(msg)) return;
      const newTables = tables.slice(0, clamped);
      sendUpdate(newTables, `تعديل عدد الطاولات إلى ${clamped}`);
      if (!newTables.find(t => t.id === selTblId)) {
        setSelTblId(newTables[newTables.length - 1]?.id || null);
        setSelSeatNo(1);
      }
    } else {
      const toAdd = clamped - tables.length;
      const newTbls: Table[] = Array.from({ length: toAdd }, (_, i) => ({
        id: `tbl-extra-${Date.now()}-${i}`,
        label: `طاولة ${tables.length + i + 1}`,
        notes: '',
        username: '',
        password: '',
        seatCount: globalSeatCount,
        quota: { admin: 0, t1: 0, t2: 0 },
        seats: Array.from({ length: globalSeatCount }, (_, j) => ({ seatNo: j + 1, guest: null })),
      }));
      sendUpdate([...tables, ...newTbls], `إضافة ${toAdd} طاولة جديدة`);
    }
  };

  const handleUpdateQuota = (role: TableRole, nextValue: number) => {
    if (!selTbl) return;

    const normalizedValue = Math.max(0, Math.floor(Number.isFinite(nextValue) ? nextValue : 0));
    const otherQuotaTotal = tblQuotaTotal(selTbl) - selTbl.quota[role];
    const maxAllowed = Math.max(0, selTbl.seatCount - otherQuotaTotal);
    const cappedValue = Math.min(normalizedValue, maxAllowed);

    handleUpdateTable({
      quota: {
        ...selTbl.quota,
        [role]: cappedValue,
      },
    });
  };

  const handleUpdateSeat = (guest: Guest | null) => {
    if (!selTbl || !selSeatNo) return;
    const newTables = tables.map(t => {
      if (t.id !== selTblId) return t;
      return {
        ...t,
        seats: t.seats.map(s => s.seatNo === selSeatNo ? { ...s, guest } : s)
      };
    });
    const logMsg = guest 
      ? `تسجيل: ${guest.name} (${ROLE_LABELS[guest.role]}) — ${selTbl.label}`
      : `مسح مقعد ${selSeatNo} — ${selTbl.label}`;
    sendUpdate(newTables, logMsg);
  };

  const handleMove = (targetTblId: string, targetSeatNo: number) => {
    if (!selTbl || !selSeat?.guest) return;
    const guest = selSeat.guest;
    
    const newTables = tables.map(t => {
      if (t.id === selTblId) {
        return {
          ...t,
          seats: t.seats.map(s => s.seatNo === selSeatNo ? { ...s, guest: null } : s)
        };
      }
      if (t.id === targetTblId) {
        return {
          ...t,
          seats: t.seats.map(s => s.seatNo === targetSeatNo ? { ...s, guest } : s)
        };
      }
      return t;
    });

    const targetLabel = tables.find(t => t.id === targetTblId)?.label;
    sendUpdate(newTables, `نقل ${guest.name} → ${targetLabel} مقعد ${targetSeatNo}`);
    setSelTblId(targetTblId);
    setSelSeatNo(targetSeatNo);
  };

  const handleSwap = (targetTblId: string, targetSeatNo: number) => {
    if (!selTbl || !selSeat?.guest) return;
    const targetTbl = tables.find(t => t.id === targetTblId);
    if (!targetTbl) return;
    const targetSeat = targetTbl.seats.find(s => s.seatNo === targetSeatNo);
    if (!targetSeat) return;

    const guestA = selSeat.guest;
    const guestB = targetSeat.guest;

    const newTables = tables.map(t => {
      if (t.id === selTblId) {
        return { ...t, seats: t.seats.map(s => s.seatNo === selSeatNo ? { ...s, guest: guestB } : s) };
      }
      if (t.id === targetTblId) {
        return { ...t, seats: t.seats.map(s => s.seatNo === targetSeatNo ? { ...s, guest: guestA } : s) };
      }
      return t;
    });

    const targetLabel = targetTbl.label;
    const logMsg = guestB
      ? `تبديل ${guestA.name} ↔ ${guestB.name} (${selTbl.label} ↔ ${targetLabel})`
      : `نقل ${guestA.name} → ${targetLabel} مقعد ${targetSeatNo}`;
    sendUpdate(newTables, logMsg);
  };

  // Search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setActiveResults([]);
      setIsSuggOpen(false);
      return;
    }
    const res: any[] = [];
    tables.forEach(t => {
      t.seats.forEach(s => {
        if (s.guest?.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          res.push({ tblId: t.id, seatNo: s.seatNo, name: s.guest.name, tblLabel: t.label, role: s.guest.role });
        }
      });
    });
    setActiveResults(res);
    setIsSuggOpen(true);
  }, [searchQuery, tables]);

  const rc = selTbl ? roleCount(selTbl) : { admin: 0, t1: 0, t2: 0 };

  return (
    <AppShell urls={urls} translations={t} wideLayout={true} fullWidth={true} hideNav={true} {...shell}>
      <Head title={`إدارة المقاعد — ${event.name}`} />

      {/* Inject Design CSS */}
      <style>{`
        :root {
          --navy: #1a1f5e;
          --blue: #1e3a8a;
          --teal-dk: #0e7490;
          --teal: #06b6d4;
          --mint: #2dd4bf;
          --bg: #f0f6fa;
          --surf: #ffffff;
          --surf2: #f4fbfd;
          --bdr: #c8e6ef;
          --txt: #0f1f3d;
          --muted: #4a6a7c;
          --r: 10px;
          --rs: 7px;
          --admin-bg: #b9dbfb;
          --admin-txt: #073764;
          --admin-bdr: #1f6fbe;
          --t1-bg: #cfe7a8;
          --t1-txt: #355f0a;
          --t1-bdr: #5f8f18;
          --t2-bg: #d7d2ff;
          --t2-txt: #30277a;
          --t2-bdr: #675ed2;
          --warn-bg: #ffd9b0;
          --warn-txt: #8a4800;
          --warn-bdr: #f08a18;
        }

        .nca-seats-body {
          font-family: 'Cairo', sans-serif;
          background: var(--bg);
          color: var(--txt);
          margin: 0;
          padding: 0;
        }

        .app-hdr {
          background: linear-gradient(135deg, var(--navy), var(--blue) 55%, var(--teal-dk));
          border-bottom: 3px solid var(--teal);
          padding: 0 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          min-height: 68px;
          margin-bottom: 16px;
          border-radius: var(--r);
        }

        .hdr-title { font-size: 1.1rem; font-weight: 900; color: #fff; }
        .hdr-sub { font-size: 0.72rem; color: var(--teal); font-weight: 700; }
        .hdr-badge {
          display: flex; align-items: center; gap: 6px; padding: 5px 12px;
          border-radius: 20px; background: rgba(6, 182, 212, .15); border: 1px solid rgba(6, 182, 212, .35);
        }
        .hdr-badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: .35 } }
        .hdr-badge span { font-size: 0.75rem; color: var(--teal); font-weight: 700; }

        .layout {
          display: grid; grid-template-columns: 1fr min(380px, 100%);
          gap: 16px; padding: 16px 0; max-width: 100%; align-items: start;
          direction: rtl;
        }

        .stat-card {
          background: var(--surf); border-radius: var(--r); padding: 12px 14px;
          border: 1px solid var(--bdr); border-top: 3px solid var(--teal);
        }
        .stat-card h4 { font-size: 0.7rem; font-weight: 700; color: var(--muted); margin-bottom: 4px; }
        .stat-card .val {
          font-size: 1.5rem; font-weight: 900;
          background: linear-gradient(135deg, var(--navy), var(--teal-dk));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }

        .toolbar { background: var(--surf); border-radius: var(--r); padding: 14px 16px; border: 1px solid var(--bdr); box-shadow: 0 1px 4px rgba(14, 116, 144, .08); }
        .floor-plan {
          display: grid; grid-template-columns: repeat(12, minmax(78px, 1fr));
          gap: 10px 8px; background: var(--surf2); border: 1px solid var(--bdr);
          border-radius: var(--r); padding: 14px; direction: ltr; min-width: 1080px;
        }

        .tbl-card {
          position: relative; border-radius: var(--r); border: 2px solid var(--bdr);
          background: var(--surf); padding: 8px 6px 6px; cursor: pointer;
          transition: all .15s; display: flex; flex-direction: column; gap: 5px;
        }
        .tbl-card:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(14, 116, 144, .15); }
        .tbl-card.st-ok { border-color: #4f8ff7; background: linear-gradient(180deg, #fff, #dcebff); }
        .tbl-card.st-warn { border-color: #f08a18; background: linear-gradient(180deg, #fff, #fff0df); }
        .tbl-card.st-full { border-color: #E24B4A; background: linear-gradient(180deg, #fff, #FCEBEB); }
        .tbl-card.selected { box-shadow: 0 0 0 3px rgba(6, 182, 212, .3); }
        .tbl-card.sh-hit { border-color: #f59e0b !important; box-shadow: 0 0 0 4px rgba(245, 158, 11, .2) !important; }

        .tbl-badge {
          position: absolute; top: -11px; left: 50%; transform: translateX(-50%);
          font-size: 0.56rem; font-weight: 800; padding: 1px 8px; border-radius: 20px;
          white-space: nowrap; z-index: 3; background: var(--teal-dk); color: #fff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, .18);
        }
        .st-ok .tbl-badge { background: #2f6fd8; }
        .st-warn .tbl-badge { background: #c56b08; }
        .st-full .tbl-badge { background: #A32D2D; }

        .sm {
          width: 16px; height: 16px; border-radius: 3px; border: 1px solid transparent;
          transition: transform .1s; display: flex; align-items: center; justify-content: center;
        }
        .sm.empty { background: #dcebff; border-color: #4f8ff7; }
        .sm.occ-admin { background: var(--admin-bg); border-color: var(--admin-bdr); }
        .sm.occ-t1 { background: var(--t1-bg); border-color: var(--t1-bdr); }
        .sm.occ-t2 { background: var(--t2-bg); border-color: var(--t2-bdr); }
        .sm.sh { background: #fef3c7 !important; border-color: #f59e0b !important; box-shadow: 0 0 0 2px rgba(245, 158, 11, .25); }
        .sm.ci { position: relative; }
        .sm.ci::after { content: '✓'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 900; color: #16a34a; line-height: 1; pointer-events: none; }

        .side-panel {
          position: sticky; top: 14px; background: var(--surf); border-radius: var(--r);
          border: 1px solid var(--bdr); box-shadow: 0 8px 32px rgba(14, 116, 144, .12);
          overflow: hidden; display: flex; flex-direction: column; max-height: calc(100vh - 30px);
        }
        @media(max-width:1024px) { 
          .layout { grid-template-columns: 1fr; padding: 12px; gap: 12px; } 
          .side-panel { position: static; max-height: none; }
          .floor-plan { min-width: 800px; padding: 8px; gap: 6px; }
          .app-hdr { padding: 12px 16px; justify-content: center; margin-bottom: 12px; }
          .app-hdr .flex { flex-wrap: wrap; justify-content: center; }
          .stat-card { padding: 8px 10px; }
          .stat-card .val { font-size: 1.25rem; }
        }
        @media(max-width:640px) {
           .app-hdr { flex-direction: column; align-items: stretch; text-align: center; gap: 16px; }
           .app-hdr > div { justify-content: center; }
           .hdr-badge { margin: 0 auto; }
           .hdr-title { font-size: 1rem; }
           .hdr-sub { font-size: 0.65rem; }
           .stat-card { padding: 6px 8px; }
           .stat-card h4 { font-size: 0.6rem; }
           .stat-card .val { font-size: 1.15rem; }
        }
        
        /* Smooth Scrollbar for floor plan */
        .scroll-container {
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 8px;
          -webkit-overflow-scrolling: touch;
          direction: ltr; /* Ensure scrolling is LTR even if page is RTL */
        }
        .scroll-container::-webkit-scrollbar { height: 6px; }
        .scroll-container::-webkit-scrollbar-track { background: var(--surf2); border-radius: 10px; }
        .scroll-container::-webkit-scrollbar-thumb { background: var(--bdr); border-radius: 10px; }
        .scroll-container::-webkit-scrollbar-thumb:hover { background: var(--teal); }
        .sp-hdr {
          padding: 12px 18px; border-bottom: 1px solid var(--bdr);
          background: linear-gradient(135deg, var(--navy), var(--teal-dk));
          display: flex; align-items: center; justify-content: space-between;
        }
        .sp-hdr h3 { font-size: 0.95rem; font-weight: 800; color: #fff; }
        .psec { padding: 12px 16px; border-bottom: 1px solid var(--bdr); }
        .sp-body { flex: 1; min-height: 0; overflow-y: auto; }
        .sp-body::-webkit-scrollbar { width: 5px; }
        .sp-body::-webkit-scrollbar-track { background: var(--surf2); }
        .sp-body::-webkit-scrollbar-thumb { background: var(--bdr); border-radius: 10px; }
        .sp-body::-webkit-scrollbar-thumb:hover { background: var(--teal); }
        .sec-ttl {
          font-size: 0.64rem; font-weight: 800; text-transform: uppercase;
          letter-spacing: .07em; color: var(--teal-dk); margin-bottom: 9px;
          padding-bottom: 5px; border-bottom: 1.5px solid var(--bdr);
        }
        .quota-box { border-radius: var(--rs); padding: 8px 5px; text-align: center; border: 1.5px solid transparent; }
        .quota-box .qval { font-size: 1.15rem; font-weight: 900; }
        .quota-box .qlabel { font-size: 0.62rem; font-weight: 700; margin-top: 2px; }
        .quota-box .qdiff { font-size: 0.64rem; font-weight: 800; margin-top: 2px; }
        .qb-admin { background: var(--admin-bg); color: var(--admin-txt); border-color: var(--admin-bdr); }
        .qb-t1 { background: var(--t1-bg); color: var(--t1-txt); border-color: var(--t1-bdr); }
        .qb-t2 { background: var(--t2-bg); color: var(--t2-txt); border-color: var(--t2-bdr); }
        .qb-warn { background: var(--warn-bg); color: var(--warn-txt); border-color: var(--warn-bdr); }
        .qb-muted { background: #eceff3; color: #5b6573; border-color: #c7ced8; }
      `}</style>

      <div className="nca-seats-body" dir="rtl">
        
        {/* Design Header */}
        <header className="app-hdr">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ background: '#fff', borderRadius: '8px', padding: '5px 12px', border: '2px solid rgba(6,182,212,.3)', boxShadow: '0 0 0 3px rgba(6,182,212,.1)' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 900, color: '#1a1f5e' }}>NCA</span>
            </div>
            <div style={{ width: '1px', height: '36px', background: 'rgba(6,182,212,.3)' }}></div>
            <div>
              <div className="hdr-title">{event.name}</div>
              <div className="hdr-sub">الهيئة الوطنية للأمن السيبراني — تشغيل القاعة في الوقت الفعلي</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hdr-badge">
              <div className="dot"></div><span>مباشر</span>
            </div>
            <Button onClick={handleAddTable} variant="outline" className="h-8 border-cyan-200 bg-white/10 text-white hover:bg-white/20">
              + إضافة طاولة
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline" className="h-8 border-cyan-200 bg-white/10 text-white hover:bg-white/20">
              تحديث
            </Button>
            <a href={urls.events} className="text-white/70 hover:text-white px-2 py-1 text-sm font-bold">الرجوع</a>
          </div>
        </header>

        <div className="layout">
          
          <div className="flex flex-col gap-4 min-w-0">
            
            {/* Stats Cards Row */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
              <SummaryCard label="الطاولات" value={tables.length} />
              <SummaryCard label="غير مكتملة" value={stats.ok} />
              <SummaryCard label="خلل توزيع" value={stats.issues} />
              <SummaryCard label="مكتملة" value={stats.full} />
              <SummaryCard label="إجمالي المقاعد" value={stats.totalSeats} />
              <SummaryCard label="مشغولة" value={stats.occupied} />
              <SummaryCard label="متبقية" value={stats.totalSeats - stats.occupied} />
              <SummaryCard label="حضور" value={stats.occupied} />
            </div>

            {/* Toolbar */}
            <div className="toolbar">
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px' }}>
                البحث عن شخص
              </label>
              <div className="relative">
                <input 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="اكتب اسم الشخص للبحث..." 
                  className="w-full p-2 border border-blue-100 rounded-md outline-none focus:ring-2 focus:ring-cyan-500/20"
                />
                
                {isSuggOpen && activeResults.length > 0 && (
                  <div className="absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                    {activeResults.map((res, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setSelTblId(res.tblId);
                          setSelSeatNo(res.seatNo);
                          setIsSuggOpen(false);
                          setSearchQuery(res.name);
                        }}
                        className="flex w-full flex-col p-3 text-right hover:bg-slate-50 border-b last:border-0"
                      >
                        <span className="text-sm font-bold">{res.name}</span>
                        <span className="text-xs text-slate-500">{res.tblLabel} — مقعد {res.seatNo} · {ROLE_LABELS[res.role]}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Global Settings */}
              <div style={{ borderTop: '1px solid var(--bdr)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>الإعدادات العامة للقاعة</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>عدد الطاولات ({tables.length} حالياً)</label>
                    <div className="flex gap-1">
                      <button
                        className="h-8 w-8 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30 text-sm font-bold"
                        onClick={() => handleSetTableCount(tables.length - 1)}
                        disabled={tables.length <= 1}
                      >−</button>
                      <input
                        type="number" min={1} max={FLOOR_SLOTS.length}
                        value={tables.length}
                        onChange={e => handleSetTableCount(Number(e.target.value))}
                        className="w-16 h-8 rounded border border-blue-100 text-center text-sm font-bold"
                      />
                      <button
                        className="h-8 w-8 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30 text-sm font-bold"
                        onClick={() => handleSetTableCount(tables.length + 1)}
                        disabled={tables.length >= FLOOR_SLOTS.length}
                      >+</button>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>مقاعد لكل طاولة</label>
                    <div className="flex gap-1 flex-wrap">
                      <button
                        className="h-8 w-8 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30 text-sm font-bold"
                        onClick={() => setGlobalSeatCount(v => Math.max(1, v - 1))}
                        disabled={globalSeatCount <= 1}
                      >−</button>
                      <input
                        type="number" min={1} max={30}
                        value={globalSeatCount}
                        onChange={e => setGlobalSeatCount(Math.max(1, Math.min(30, Number(e.target.value))))}
                        className="w-16 h-8 rounded border border-blue-100 text-center text-sm font-bold"
                      />
                      <button
                        className="h-8 w-8 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30 text-sm font-bold"
                        onClick={() => setGlobalSeatCount(v => Math.min(30, v + 1))}
                        disabled={globalSeatCount >= 30}
                      >+</button>
                      <button
                        onClick={handleApplyGlobalSeatCount}
                        className="h-8 px-3 text-xs font-bold rounded-md text-white"
                        style={{ background: 'linear-gradient(135deg, var(--teal-dk), var(--teal))', whiteSpace: 'nowrap' }}
                      >تطبيق على الكل</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hall Map */}
            <div className="bg-white rounded-[10px] border border-blue-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-[0.95rem] font-extrabold text-slate-800">مخطط القاعة</h2>
                <span className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-100 text-[0.68rem] font-bold text-slate-500">
                  {tables.length} طاولة
                </span>
              </div>
              <p className="text-[0.76rem] text-slate-500 mb-3">انقر على طاولة لعرض التفاصيل وإدارة المقاعد والأدوار. اللون البرتقالي = زيادة أو خلل في توزيع الأدوار.</p>
              
              <div className="scroll-container">
                <div className="floor-plan">
                  {/* Control Area */}
                  <div className="flex items-center justify-center border-2 border-dashed border-blue-100 rounded-md text-slate-400 text-[0.7rem] font-bold min-h-[62px]" style={{ gridColumn: '5/9', gridRow: '1/2', direction: 'rtl' }}>
                    منطقة التحكم
                  </div>

                  {/* Tables */}
                  {tables.map((t, i) => {
                    const slot = FLOOR_SLOTS[i];
                    if (!slot) return null;
                    const st = getTblStatus(t);
                    const isSelected = selTblId === t.id;
                    const isHit = isSelected && searchQuery.trim().length >= 2;
                    
                    return (
                      <div 
                        key={t.id}
                        onClick={() => setSelTblId(t.id)}
                        style={{ gridRow: `${slot[0] + 1}`, gridColumn: `${slot[1] + 1}` }}
                        className={`tbl-card st-${st} ${isSelected ? 'selected' : ''} ${isHit ? 'sh-hit' : ''}`}
                      >
                        <span className="tbl-badge">{t.label}</span>
                        <div className="flex flex-wrap gap-[3px] justify-center mt-1">
                          {t.seats.map((s) => (
                            <div 
                              key={s.seatNo}
                              onClick={(e) => { e.stopPropagation(); setSelTblId(t.id); setSelSeatNo(s.seatNo); }}
                              className={`sm ${!s.guest ? 'empty' : `occ-${s.guest.role}`} ${isSelected && s.seatNo === selSeatNo && searchQuery.trim().length >= 2 ? 'sh' : ''} ${s.guest && checkedInIds.includes(Number(s.guest.id)) ? 'ci' : ''}`}
                              title={s.guest ? `${s.guest.name} (${ROLE_LABELS[s.guest.role]})` : `مقعد ${s.seatNo}`}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>

          {/* Side Panel */}
          <aside className="side-panel">
            <div className="sp-hdr">
              <h3>{selTbl?.label || 'تفاصيل الطاولة'}</h3>
              <div className="live-badge">
                <div className="ldot" /><span>مباشر</span>
              </div>
            </div>
            
            <div className="sp-body">
               {selTbl ? (
                 <>
                   <div className="psec">
                      <div className="sec-ttl">معلومات الطاولة</div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px' }}>اسم الطاولة</label>
                      <input 
                        value={selTbl.label} 
                        onChange={e => handleUpdateTable({ label: e.target.value })}
                        className="w-full p-2 border border-blue-100 rounded-md text-sm"
                      />
                      <div className="grid grid-cols-2 gap-y-1 text-xs mt-3">
                         <span className="text-slate-400">المقاعد الكلية</span><span className="font-bold">{selTbl.seatCount}</span>
                         <span className="text-slate-400">مشغولة</span><span className="font-bold">{tblOccupied(selTbl)}</span>
                         <span className="text-slate-400">متبقية</span><span className="font-bold">{selTbl.seatCount - tblOccupied(selTbl)}</span>
                      </div>
                       <div className="flex items-center justify-between mt-3">
                         <span className="text-[0.72rem] text-slate-500 font-bold">عدد المقاعد</span>
                         <div className="flex gap-1">
                           <button className="h-7 w-7 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30" onClick={() => handleUpdateSeatCount(selTbl.seatCount - 1)} disabled={selTbl.seatCount <= Math.max(1, tblOccupied(selTbl))}>−</button>
                           <span className="h-7 px-2 flex items-center text-sm font-bold">{selTbl.seatCount}</span>
                           <button className="h-7 w-7 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50" onClick={() => handleUpdateSeatCount(selTbl.seatCount + 1)}>+</button>
                         </div>
                      </div>
                        <button
                          onClick={handleRemoveTable}
                          className="w-full mt-3 p-2 text-xs font-bold text-red-600 border border-red-200 rounded-md bg-red-50 hover:bg-red-100"
                        >
                          حذف الطاولة
                        </button>
                      <textarea 
                        value={selTbl.notes} 
                        onChange={e => handleUpdateTable({ notes: e.target.value })}
                        placeholder="ملاحظات خاصة بهذه الطاولة"
                        className="w-full mt-3 p-2 border border-blue-100 rounded-md text-sm min-h-[60px]"
                      />
                   </div>

                   <div className="psec">
                      <div className="sec-ttl">بيانات تسجيل الدخول للطاولة</div>
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px' }}>اسم المستخدم</label>
                      <input 
                        value={selTbl.username || ''}
                        onChange={e => handleUpdateTable({ username: e.target.value })}
                        className="w-full p-2 border border-blue-100 rounded-md text-sm" 
                      />
                      <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', margin: '8px 0 5px' }}>كلمة المرور</label>
                      <input 
                        value={selTbl.password || ''}
                        onChange={e => handleUpdateTable({ password: e.target.value })}
                        className="w-full p-2 border border-blue-100 rounded-md text-sm" 
                      />
                   </div>

                   <div className="psec">
                      <div className="sec-ttl">توزيع الأدوار والحصص</div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <QuotaBox label={ROLE_LABELS.admin} actual={rc.admin} quota={selTbl.quota.admin} cls="qb-admin" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                        <QuotaBox label={ROLE_LABELS.t1} actual={rc.t1} quota={selTbl.quota.t1} cls="qb-t1" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                        <QuotaBox label={ROLE_LABELS.t2} actual={rc.t2} quota={selTbl.quota.t2} cls="qb-t2" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                      </div>
                      <div className="mt-3 rounded-md border border-blue-100 bg-slate-50 p-3">
                        <div className="flex items-center justify-between text-[0.72rem] font-bold text-slate-600">
                          <span>ضبط السعة لكل فئة</span>
                          <span>{tblQuotaTotal(selTbl)} / {selTbl.seatCount}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <QuotaEditor
                            label={ROLE_LABELS.admin}
                            value={selTbl.quota.admin}
                            maxValue={selTbl.seatCount - (tblQuotaTotal(selTbl) - selTbl.quota.admin)}
                            onChange={(value) => handleUpdateQuota('admin', value)}
                          />
                          <QuotaEditor
                            label={ROLE_LABELS.t1}
                            value={selTbl.quota.t1}
                            maxValue={selTbl.seatCount - (tblQuotaTotal(selTbl) - selTbl.quota.t1)}
                            onChange={(value) => handleUpdateQuota('t1', value)}
                          />
                          <QuotaEditor
                            label={ROLE_LABELS.t2}
                            value={selTbl.quota.t2}
                            maxValue={selTbl.seatCount - (tblQuotaTotal(selTbl) - selTbl.quota.t2)}
                            onChange={(value) => handleUpdateQuota('t2', value)}
                          />
                        </div>
                        <p className="mt-2 text-[0.68rem] text-slate-500">
                          المقاعد غير الموزعة على فئة: {selTbl.seatCount - tblQuotaTotal(selTbl)}
                        </p>
                      </div>
                   </div>

                   <div className="psec">
                      <div className="sec-ttl">معلومات الضيف المحدد</div>
                      <div className="grid grid-cols-2 gap-y-1 text-xs mb-2">
                        <span className="text-slate-400">الطاولة</span><span className="font-bold">{selTbl.label}</span>
                        <span className="text-slate-400">المقعد</span><span className="font-bold">{selSeatNo}</span>
                      </div>
                      <input 
                        value={selSeat?.guest?.name || ''} 
                        onChange={e => handleUpdateSeat({ ...selSeat?.guest || { id: Date.now(), name: '', role: 'admin' }, name: e.target.value })}
                        placeholder="اسم الضيف"
                        className="w-full p-2 border border-blue-100 rounded-md text-sm mb-2"
                      />
                      <select 
                        value={selSeat?.guest?.role || ''}
                        onChange={e => handleUpdateSeat({ ...selSeat?.guest || { id: Date.now(), name: '', role: 'admin' }, role: e.target.value })}
                        className="w-full p-2 border border-blue-100 rounded-md text-sm mb-2"
                      >
                        <option value="">— اختر الدور —</option>
                        <option value="admin">{ROLE_LABELS.admin}</option>
                        <option value="t1">{ROLE_LABELS.t1}</option>
                        <option value="t2">{ROLE_LABELS.t2}</option>
                      </select>
                      <button className="btn-primary" onClick={() => handleUpdateSeat(selSeat?.guest || null)}>حفظ بيانات المقعد</button>
                      <button className="w-full mt-1.5 p-1.5 text-xs font-bold text-red-500 border border-red-100 rounded hover:bg-red-50" onClick={() => handleUpdateSeat(null)}>مسح المقعد</button>
                   </div>

                   <div className="psec">
                      <div className="sec-ttl">نقل / تبديل</div>
                      {selSeat?.guest ? (
                        <>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>إلى طاولة</label>
                              <select
                                value={moveTblId}
                                onChange={e => { setMoveTblId(e.target.value); setMoveSeatNo(1); }}
                                className="w-full p-2 border border-blue-100 rounded-md text-sm"
                              >
                                <option value="">— اختر —</option>
                                {tables.map(t => (
                                  <option key={t.id} value={t.id}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>إلى مقعد</label>
                              <select
                                value={moveSeatNo}
                                onChange={e => setMoveSeatNo(Number(e.target.value))}
                                className="w-full p-2 border border-blue-100 rounded-md text-sm"
                              >
                                {(tables.find(t => t.id === moveTblId)?.seats || []).map(s => (
                                  <option key={s.seatNo} value={s.seatNo}>
                                    مقعد {s.seatNo} {s.guest ? `(مشغول)` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => { if (moveTblId) handleMove(moveTblId, moveSeatNo); }}
                              disabled={!moveTblId}
                              className="w-full p-2 text-xs font-bold rounded-md text-white disabled:opacity-40"
                              style={{ background: 'linear-gradient(135deg, var(--teal-dk), var(--teal))' }}
                            >نقل</button>
                            <button
                              onClick={() => { if (moveTblId) handleSwap(moveTblId, moveSeatNo); }}
                              disabled={!moveTblId}
                              className="w-full p-2 text-xs font-bold rounded-md border border-blue-100 bg-white hover:bg-slate-50 disabled:opacity-40"
                            >تبديل</button>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-slate-400 text-center py-2">اختر مقعد مشغول للنقل أو التبديل</p>
                      )}
                   </div>

                   <div className="psec">
                        <button
                          onClick={() => setColorGuideOpen(!colorGuideOpen)}
                          className="w-full cursor-pointer bg-transparent border-none p-0 text-right"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '.07em', color: 'var(--teal-dk)', marginBottom: colorGuideOpen ? 9 : 0, paddingBottom: 5, borderBottom: '1.5px solid var(--bdr)' }}
                        >
                          <span>دليل الألوان</span>
                          <span style={{ fontSize: '0.7rem', transition: 'transform .2s', transform: colorGuideOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                        </button>
                        {colorGuideOpen && (
                       <div className="flex flex-col gap-2">
                         {/* Role Colors */}
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'var(--admin-bg)', border: '1.5px solid var(--admin-bdr)', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700 flex-1">{ROLE_LABELS.admin}</span>
                           <span className="text-[0.65rem] text-slate-400 font-semibold">الحصة: {selTbl.quota.admin} لكل طاولة</span>
                         </div>
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'var(--t1-bg)', border: '1.5px solid var(--t1-bdr)', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700 flex-1">{ROLE_LABELS.t1}</span>
                           <span className="text-[0.65rem] text-slate-400 font-semibold">الحصة: {selTbl.quota.t1} لكل طاولة</span>
                         </div>
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'var(--t2-bg)', border: '1.5px solid var(--t2-bdr)', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700 flex-1">{ROLE_LABELS.t2}</span>
                           <span className="text-[0.65rem] text-slate-400 font-semibold">الحصة: {selTbl.quota.t2} لكل طاولة</span>
                         </div>

                         <div style={{ borderTop: '1px solid var(--bdr)', margin: '4px 0' }} />

                         {/* Table Status Colors */}
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'linear-gradient(180deg, #fff, #dcebff)', border: '1.5px solid #4f8ff7', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700">الطاولة غير مكتملة</span>
                         </div>
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'linear-gradient(180deg, #fff, #fff0df)', border: '1.5px solid #f08a18', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700">خلل أو زيادة في التوزيع</span>
                         </div>
                         <div className="flex items-center gap-2.5">
                           <span style={{ width: 16, height: 16, borderRadius: 3, background: 'linear-gradient(180deg, #fff, #FCEBEB)', border: '1.5px solid #E24B4A', flexShrink: 0 }} />
                           <span className="text-xs font-bold text-slate-700">طاولة مكتملة</span>
                         </div>
                       </div>
                        )}
                    </div>

                   <div className="psec bg-slate-50/30">
                      <div className="sec-ttl">سجل النشاط</div>
                      <div className="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto pl-1">
                        {activity.map((act, i) => (
                          <div key={i} className="bg-white border border-blue-50 p-2 rounded-md">
                             <div className="text-[11px] font-bold text-slate-800">{act.text}</div>
                             <div className="text-[9px] text-slate-400 mt-0.5">{act.ts}</div>
                          </div>
                        ))}
                      </div>
                   </div>
                 </>
               ) : (
                 <div className="p-20 text-center text-slate-300">
                    <p>اختر طاولة للبدء</p>
                 </div>
               )}
            </div>
          </aside>

        </div>
      </div>
    </AppShell>
  );
}

function SummaryCard({ label, value }: { label: string, value: number }) {
  return (
    <div className="stat-card">
      <h4>{label}</h4>
      <div className="val">{value}</div>
    </div>
  );
}

function QuotaBox({ label, actual, quota, cls, full }: { label: string, actual: number, quota: number, cls: string, full: boolean }) {
  const diff = actual - quota;
  const isOk = diff === 0;
  const statusCls = !full ? 'qb-muted' : (!isOk ? 'qb-warn' : cls);
  const diffStr = diff > 0 ? ` +${diff}` : diff < 0 ? ` ${diff}` : '✓';
  
  return (
    <div className={`quota-box ${statusCls}`}>
      <div className="qval">{actual}</div>
      <div className="qlabel">{label}</div>
      <div className="qdiff">مطلوب: {quota} <b>{diffStr}</b></div>
    </div>
  );
}

function QuotaEditor({ label, value, maxValue, onChange }: { label: string, value: number, maxValue: number, onChange: (value: number) => void }) {
  return (
    <div className="rounded-md border border-blue-100 bg-white p-2">
      <div className="text-[0.7rem] font-bold text-slate-600 text-center">{label}</div>
      <div className="flex items-center gap-1 mt-2">
        <button
          className="h-7 w-7 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30"
          onClick={() => onChange(value - 1)}
          disabled={value <= 0}
        >−</button>
        <input
          type="number"
          min={0}
          max={Math.max(0, maxValue)}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          className="w-full h-7 rounded border border-blue-100 text-center text-sm font-bold"
        />
        <button
          className="h-7 w-7 flex items-center justify-center border border-blue-100 rounded bg-white hover:bg-slate-50 disabled:opacity-30"
          onClick={() => onChange(value + 1)}
          disabled={value >= maxValue}
        >+</button>
      </div>
    </div>
  );
}

function tblQuotaTotal(t: Table) { return t.quota.admin + t.quota.t1 + t.quota.t2; }

function tblOccupied(t: Table) { return t.seats.filter(s => s.guest).length; }
